import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Modal from '../components/Modal';
import api from '../services/api';

const formatCOP = (v) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v);

const TIPO_CONFIG = {
  estandar:         { label: 'Estándar',         color: '#00E5FF', bg: 'rgba(0,229,255,0.1)' },
  mayorista:        { label: 'Mayorista',         color: '#F59E0B', bg: 'rgba(245,158,11,0.1)' },
  contrato_especial:{ label: 'Contrato especial', color: '#8B5CF6', bg: 'rgba(139,92,246,0.1)' },
};

const TEC_CONFIG = {
  fibra:        { label: 'Fibra óptica',  icon: '🔵' },
  radio_enlace: { label: 'Radio enlace',  icon: '📡' },
  ambas:        { label: 'Ambas',         icon: '🌐' },
};

const formVacio = { nombre:'', descripcion:'', precio_mensual:'', costo_inscripcion:'0', velocidad_bajada:'', velocidad_subida:'', tecnologia:'ambas', tipo_plan:'estandar', notas_contrato:'', publicado_en_web:false };

export default function Planes() {
  const [planes, setPlanes] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(formVacio);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    const params = filtroTipo ? `?tipo_plan=${filtroTipo}` : '';
    const { data } = await api.get(`/api/planes${params}`);
    setPlanes(data);
  }, [filtroTipo]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNuevo = () => { setEditando(null); setForm(formVacio); setError(''); setModal(true); };
  const abrirEditar = (p) => {
    setEditando(p);
    setForm({ nombre:p.nombre, descripcion:p.descripcion||'', precio_mensual:p.precio_mensual, costo_inscripcion:p.costo_inscripcion||0, velocidad_bajada:p.velocidad_bajada||'', velocidad_subida:p.velocidad_subida||'', tecnologia:p.tecnologia||'ambas', tipo_plan:p.tipo_plan||'estandar', notas_contrato:p.notas_contrato||'', publicado_en_web: !!p.publicado_en_web });
    setError(''); setModal(true);
  };

  const guardar = async () => {
    if (!form.nombre || !form.precio_mensual) { setError('Nombre y precio son requeridos'); return; }
    setGuardando(true);
    try {
      if (editando) await api.put(`/api/planes/${editando.id}`, form);
      else await api.post('/api/planes', form);
      setModal(false); cargar();
    } catch (err) { setError(err.response?.data?.error || 'Error al guardar'); }
    finally { setGuardando(false); }
  };

  const eliminar = async (p) => {
    if (!window.confirm(`¿Eliminar el plan "${p.nombre}"?`)) return;
    try { await api.delete(`/api/planes/${p.id}`); cargar(); }
    catch (err) { alert(err.response?.data?.error || 'Error'); }
  };

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <Layout>
      <div style={s.header}>
        <div>
          <h1 style={s.titulo}>Planes</h1>
          <p style={s.sub}>Planes de suscripción y contratos especiales</p>
        </div>
        <button style={s.btnPrimario} onClick={abrirNuevo}>+ Nuevo Plan</button>
      </div>

      {/* Filtros por tipo */}
      <div style={s.filtros}>
        {[['','Todos'], ['estandar','Estándar'], ['mayorista','Mayorista'], ['contrato_especial','Contrato especial']].map(([val, label]) => (
          <button key={val} onClick={() => setFiltroTipo(val)}
            style={{ ...s.filtroBtn, ...(filtroTipo === val ? s.filtroBtnActivo : {}) }}>
            {label}
          </button>
        ))}
      </div>

      {/* Grid de planes */}
      <div style={s.grid}>
        {planes.map(plan => {
          const tipoCfg = TIPO_CONFIG[plan.tipo_plan] || TIPO_CONFIG.estandar;
          const tecCfg = TEC_CONFIG[plan.tecnologia] || TEC_CONFIG.ambas;
          return (
            <div key={plan.id} style={{ ...s.card, opacity: plan.activo ? 1 : 0.5 }}>
              <div style={s.cardTop}>
                <span style={{ ...s.tipoBadge, color: tipoCfg.color, background: tipoCfg.bg }}>{tipoCfg.label}</span>
                <span style={s.tecBadge}>{tecCfg.icon} {tecCfg.label}</span>
              </div>
              <div style={s.planNombre}>{plan.nombre}</div>
              {plan.descripcion && <div style={s.planDesc}>{plan.descripcion}</div>}

              {/* Velocidad */}
              {(plan.velocidad_bajada || plan.velocidad_subida) && (
                <div style={s.velocidadRow}>
                  {plan.velocidad_bajada && <span style={s.velTag}>⬇ {plan.velocidad_bajada}</span>}
                  {plan.velocidad_subida && <span style={s.velTag}>⬆ {plan.velocidad_subida}</span>}
                </div>
              )}

              {/* Precios */}
              <div style={s.precios}>
                <div>
                  <div style={s.precioVal}>{formatCOP(plan.precio_mensual)}</div>
                  <div style={s.precioLabel}>/ mes</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={s.precioValSec}>{formatCOP(plan.costo_inscripcion)}</div>
                  <div style={s.precioLabel}>instalación</div>
                </div>
              </div>

              {plan.notas_contrato && (
                <div style={s.notasContrato}>📋 {plan.notas_contrato}</div>
              )}

              {/* Se ve de un vistazo cuáles salen a la calle. Sin esto habría que abrir cada
                  plan para saberlo, que es justo lo que pasaba con la regla del nombre. */}
              <div style={{ marginBottom:'10px' }}>
                <span style={{ fontSize:'11.5px', padding:'3px 9px', borderRadius:'999px',
                               background: plan.publicado_en_web ? 'rgba(16,185,129,0.14)' : 'rgba(255,255,255,0.05)',
                               color: plan.publicado_en_web ? '#6EE7B7' : 'rgba(255,255,255,0.45)',
                               border: `1px solid ${plan.publicado_en_web ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.1)'}` }}>
                  {plan.publicado_en_web ? '🌐 en la página' : '🔒 solo interno'}
                </span>
              </div>

              <div style={s.cardFooter}>
                <span style={s.clientesTag}>👥 {plan.total_clientes} clientes</span>
                <div style={s.acciones}>
                  <button style={s.btnEdit} onClick={() => abrirEditar(plan)}>Editar</button>
                  <button style={s.btnDel} onClick={() => eliminar(plan)}>Eliminar</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      <Modal abierto={modal} onCerrar={() => setModal(false)} titulo={editando ? 'Editar Plan' : 'Nuevo Plan'} ancho="580px">
        <div style={s.formGrid}>
          <div style={{ ...s.campo, gridColumn:'1/-1' }}>
            <label style={s.label}>Nombre del plan *</label>
            <input style={s.input} value={form.nombre} onChange={e => f('nombre',e.target.value)} placeholder="Ej: Plan Hogar 10MB" />
          </div>
          <div style={s.campo}>
            <label style={s.label}>Tipo de plan</label>
            <select style={s.input} value={form.tipo_plan} onChange={e => f('tipo_plan',e.target.value)}>
              <option value="estandar">Estándar</option>
              <option value="mayorista">Mayorista / Revendedor</option>
              <option value="contrato_especial">Contrato especial</option>
            </select>
          </div>
          <div style={s.campo}>
            <label style={s.label}>Tecnología</label>
            <select style={s.input} value={form.tecnologia} onChange={e => f('tecnologia',e.target.value)}>
              <option value="ambas">🌐 Ambas</option>
              <option value="fibra">🔵 Fibra óptica</option>
              <option value="radio_enlace">📡 Radio enlace</option>
            </select>
          </div>
          <div style={s.campo}>
            <label style={s.label}>Velocidad de bajada</label>
            <input style={s.input} value={form.velocidad_bajada} onChange={e => f('velocidad_bajada',e.target.value)} placeholder="Ej: 10 Mbps" />
          </div>
          <div style={s.campo}>
            <label style={s.label}>Velocidad de subida</label>
            <input style={s.input} value={form.velocidad_subida} onChange={e => f('velocidad_subida',e.target.value)} placeholder="Ej: 5 Mbps" />
          </div>
          <div style={s.campo}>
            <label style={s.label}>Precio mensual (COP) *</label>
            <input style={s.input} type="number" value={form.precio_mensual} onChange={e => f('precio_mensual',e.target.value)} placeholder="0" />
          </div>
          <div style={s.campo}>
            <label style={s.label}>Costo de instalación (COP)</label>
            <input style={s.input} type="number" value={form.costo_inscripcion} onChange={e => f('costo_inscripcion',e.target.value)} placeholder="0" />
          </div>
          <div style={{ ...s.campo, gridColumn:'1/-1' }}>
            <label style={s.label}>Descripción</label>
            <input style={s.input} value={form.descripcion} onChange={e => f('descripcion',e.target.value)} placeholder="Descripción del plan" />
          </div>
          {form.tipo_plan !== 'estandar' && (
            <div style={{ ...s.campo, gridColumn:'1/-1' }}>
              <label style={s.label}>Notas del contrato</label>
              <textarea style={{ ...s.input, minHeight:'60px', resize:'vertical' }} value={form.notas_contrato} onChange={e => f('notas_contrato',e.target.value)} placeholder="Condiciones especiales, vigencia, acuerdos..." />
            </div>
          )}

          {/* Antes esto lo decidía el NOMBRE: la página publicaba todo plan activo salvo los
              que llevaran «abonado». O sea que para no publicar uno había que bautizarlo de
              cierta forma, y un plan interno con otro nombre salía a la calle solo.
              Un plan nuevo nace SIN publicar: publicar de más lo ven los clientes; no
              publicar solo se ve aquí y se arregla con un clic. */}
          <div style={{ ...s.campo, gridColumn:'1/-1' }}>
            <label style={{ display:'flex', alignItems:'flex-start', gap:'10px', cursor:'pointer',
                            padding:'12px', borderRadius:'10px',
                            background: form.publicado_en_web ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.03)',
                            border: `1px solid ${form.publicado_en_web ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.1)'}` }}>
              <input type="checkbox" checked={!!form.publicado_en_web}
                onChange={e => f('publicado_en_web', e.target.checked)}
                style={{ width:'18px', height:'18px', marginTop:'1px', cursor:'pointer', flexShrink:0 }} />
              <span>
                <span style={{ fontWeight:'600', color: form.publicado_en_web ? '#6EE7B7' : 'rgba(255,255,255,0.8)' }}>
                  {form.publicado_en_web ? '🌐 Se muestra en la página' : '🔒 No se muestra en la página'}
                </span>
                <span style={{ display:'block', fontSize:'12px', color:'rgba(255,255,255,0.5)', marginTop:'3px' }}>
                  {form.publicado_en_web
                    ? 'Cualquiera que entre a linkustelecom.com/internet lo va a ver.'
                    : 'Solo lo ves aquí. Sirve igual para asignárselo a un cliente y para cobrarle.'}
                </span>
              </span>
            </label>
          </div>
        </div>
        {error && <div style={s.error}>{error}</div>}
        <div style={s.modalFooter}>
          <button style={s.btnSec} onClick={() => setModal(false)}>Cancelar</button>
          <button style={s.btnPrimario} onClick={guardar} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar'}</button>
        </div>
      </Modal>
    </Layout>
  );
}

const s = {
  header: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom:'24px' },
  titulo: { fontSize:'24px', fontWeight:'700', marginBottom:'4px' },
  sub: { color:'rgba(255,255,255,0.4)', fontSize:'14px' },
  btnPrimario: { background:'linear-gradient(135deg,#00E5FF,#6366F1)', border:'none', borderRadius:'10px', padding:'10px 20px', color:'#fff', fontSize:'14px', fontWeight:'600', cursor:'pointer' },
  btnSec: { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'10px', padding:'10px 18px', color:'#fff', fontSize:'14px', cursor:'pointer' },
  filtros: { display:'flex', flexWrap: 'wrap', gap:'8px', marginBottom:'20px' },
  filtroBtn: { background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'8px', padding:'7px 14px', color:'rgba(255,255,255,0.5)', fontSize:'13px', cursor:'pointer' },
  filtroBtnActivo: { background:'rgba(0,229,255,0.1)', border:'1px solid rgba(0,229,255,0.3)', color:'#00E5FF' },
  grid: { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(min(100%,300px),1fr))', gap:'16px' },
  card: { background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'16px', padding:'20px' },
  cardTop: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' },
  tipoBadge: { fontSize:'11px', fontWeight:'700', padding:'3px 10px', borderRadius:'20px' },
  tecBadge: { fontSize:'11px', color:'rgba(255,255,255,0.4)' },
  planNombre: { fontSize:'16px', fontWeight:'700', marginBottom:'4px' },
  planDesc: { fontSize:'12px', color:'rgba(255,255,255,0.4)', marginBottom:'10px' },
  velocidadRow: { display:'flex', gap:'8px', marginBottom:'12px' },
  velTag: { fontSize:'12px', background:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.7)', padding:'3px 10px', borderRadius:'20px' },
  precios: { display:'flex', justifyContent:'space-between', background:'rgba(0,229,255,0.05)', borderRadius:'10px', padding:'12px 16px', marginBottom:'12px' },
  precioVal: { fontSize:'20px', fontWeight:'700', color:'#00E5FF' },
  precioValSec: { fontSize:'16px', fontWeight:'600', color:'rgba(255,255,255,0.6)' },
  precioLabel: { fontSize:'11px', color:'rgba(255,255,255,0.4)', marginTop:'2px' },
  notasContrato: { fontSize:'11px', color:'rgba(139,92,246,0.8)', background:'rgba(139,92,246,0.08)', borderRadius:'6px', padding:'6px 10px', marginBottom:'10px' },
  cardFooter: { display:'flex', justifyContent:'space-between', alignItems:'center' },
  clientesTag: { fontSize:'12px', color:'rgba(255,255,255,0.4)' },
  acciones: { display:'flex', gap:'8px' },
  btnEdit: { background:'rgba(99,102,241,0.15)', border:'none', borderRadius:'6px', padding:'5px 12px', color:'#A5B4FC', fontSize:'12px', cursor:'pointer' },
  btnDel: { background:'rgba(239,68,68,0.1)', border:'none', borderRadius:'6px', padding:'5px 12px', color:'#FCA5A5', fontSize:'12px', cursor:'pointer' },
  formGrid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'16px' },
  campo: { display:'flex', flexDirection:'column', gap:'6px' },
  label: { color:'rgba(255,255,255,0.6)', fontSize:'13px' },
  input: { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'8px', padding:'10px 14px', color:'#fff', fontSize:'14px', outline:'none' },
  error: { background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'8px', padding:'10px', color:'#FCA5A5', fontSize:'13px', marginBottom:'16px' },
  modalFooter: { display:'flex', gap:'12px', justifyContent:'flex-end' },
};
